<template>
  <div>
    <q-card bordered flat class="break-after">
      <div id="intercom-header-building-amenities" class="q-pa-md flex justify-between">
        <div>
          <div class="text-h6">Building Amenities</div>
          <div style="font-size: 12px">
            Compare building amenities across your competition to improve market positioning.
          </div>
        </div>
        <div id="intercom-building-amenities-actions">
          <q-select
            v-model="amenitySelections.building_amenities"
            :options="filteredAmenities.building_amenities"
            input-debounce="0"
            multiple
            hide-selected
            hide-bottom-space
            hide-hint
            use-input
            @filter="filterFn"
            label="Building Amenities"
            outlined
            dense
          >
          </q-select>
        </div>
      </div>
      <q-table
        id="intercom-building-amenities-content"
        :rows="amenityRows('building_amenities')"
        :columns="amenityColumns('building_amenities')"
        :rows-per-page-options="[0]"
        hide-bottom
        dense
        flat
        bordered
      >
        <template #header="props">
          <q-tr :props="props" :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
            <q-th v-for="col in props.cols" :key="col.name" :props="props">
              <div class="row justify-between no-wrap q-gutter-x-md">
                <div>{{ col.label }}</div>
              </div>
            </q-th>
          </q-tr>
        </template>
        <template #body="props">
          <q-tr
            :props="props"
            :class="{
              'text-bold bg-deep-purple-light text-primary':
                subjectDetail && props.row.id === subjectDetail.id
            }"
          >
            <q-td v-for="col in props.cols" :key="col.name" :props="props">
              <div>{{ col.value }}</div>
            </q-td>
          </q-tr>
        </template>
      </q-table>
    </q-card>

    <q-card bordered flat class="q-mt-lg break-after">
      <div id="intercom-header-unit-amenities" class="q-pa-md flex justify-between">
        <div>
          <div class="text-h6">Unit Amenities</div>
          <div style="font-size: 12px">
            Compare unit amenities across your competition to improve market positioning.
          </div>
        </div>
        <div id="intercom-unit-amenities-actions">
          <q-select
            v-model="amenitySelections.unit_amenities"
            :options="filteredAmenities.unit_amenities"
            label="Unit Amenities"
            input-debounce="0"
            use-input
            multiple
            hide-selected
            hide-bottom-space
            hide-hint
            outlined
            dense
            @filter="filterFn"
          />
        </div>
      </div>
      <q-table
        id="intercom-unit-amenities-content"
        :rows="amenityRows('unit_amenities')"
        :columns="amenityColumns('unit_amenities')"
        :rows-per-page-options="[0]"
        hide-bottom
        dense
        flat
        bordered
      >
        <template #header="props">
          <q-tr :props="props" :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
            <q-th v-for="col in props.cols" :key="col.name" :props="props">
              <div class="row justify-between no-wrap q-gutter-x-md">
                <div>{{ col.label }}</div>
              </div>
            </q-th>
          </q-tr>
        </template>
        <template #body="props">
          <q-tr
            :props="props"
            :class="{
              'text-bold bg-deep-purple-light text-primary':
                subjectDetail && props.row.id === subjectDetail.id
            }"
          >
            <q-td v-for="col in props.cols" :key="col.name" :props="props">
              <div>{{ col.value }}</div>
            </q-td>
          </q-tr>
        </template>
      </q-table>
    </q-card>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import type { QTableColumn } from 'quasar'
import type { PropertyDetailsResponse, Availability, Price, AvailabilityPeriod } from 'common'
import { similarityColor } from '@/utils'

export default {
  name: 'PropertyCompareAmenities',
  components: {},
  props: {
    filteredProperties: {
      type: Array<{
        prop: PropertyDetailsResponse
        units: {
          unit: Availability
          lastHistory: Price
          period: AvailabilityPeriod
        }[]
      }>,
      required: true
    },
    subjectDetail: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    }
  },
  data() {
    return {
      similarityColor,
      amenitySelections: {
        building_amenities: [],
        unit_amenities: []
      } as {
        building_amenities: { label: string; value: string }[]
        unit_amenities: { label: string; value: string }[]
      },
      filteredAmenities: {
        building_amenities: [],
        unit_amenities: []
      } as {
        building_amenities: { label: string; value: string }[]
        unit_amenities: { label: string; value: string }[]
      }
    }
  },
  computed: {
    availableAmenities() {
      const buildingAmenities = new Set<string>()
      const unitAmenities = new Set<string>()
      for (const { prop } of this.filteredProperties) {
        for (const b of prop.building_amenities || []) {
          buildingAmenities.add(b)
        }
        for (const b of prop.unit_amenities || []) {
          unitAmenities.add(b)
        }
      }
      return {
        building_amenities: [...buildingAmenities].sort(),
        unit_amenities: [...unitAmenities].sort()
      } as { building_amenities: string[]; unit_amenities: string[] }
    }
  },
  watch: {
    'amenitySelections.building_amenities'(newVal, oldVal) {
      // Yep, apparently that's valid syntax. WTF, JavaScript!
      const added = newVal.filter((_: string) => !oldVal.includes(_))
      const removed = oldVal.filter((_: string) => !newVal.includes(_))
      if (added.length === 1) {
        this.$tracker.changedAmenitySelection('added', 'building', added[0].label)
      } else if (removed.length === 1) {
        this.$tracker.changedAmenitySelection('removed', 'building', removed[0].label)
      }
    },
    'amenitySelections.unit_amenities'(newVal, oldVal) {
      const added = newVal.filter((_: string) => !oldVal.includes(_))
      const removed = oldVal.filter((_: string) => !newVal.includes(_))
      if (added.length === 1) {
        this.$tracker.changedAmenitySelection('added', 'unit', added[0].label)
      } else if (removed.length === 1) {
        this.$tracker.changedAmenitySelection('removed', 'unit', removed[0].label)
      }
    }
  },
  created() {
    this.onLoad()
  },
  methods: {
    onLoad() {
      // TODO: Compute a good mix of amenities to compare.
      const availUnitAmenities = new Set(this.availableAmenities.unit_amenities)
      const availBuildingAmenities = new Set(this.availableAmenities.building_amenities)
      this.amenitySelections.building_amenities = [
        'business_center',
        'club_house_party_room',
        'elevator',
        'fitness_center',
        'game_room',
        'swimming_pool',
        'multimedia_center',
        'parking_garage',
        'pets_allowed',
        'rooftop_space'
      ]
        .filter((_) => availBuildingAmenities.has(_))
        .map((_) => ({ label: this.toTitle(_), value: _ }))
      this.amenitySelections.unit_amenities = [
        'central_air_conditioning',
        'dishwasher',
        'efficient_appliances',
        'hardwood_floor',
        'high_ceilings',
        'patio_or_balcony',
        'stainless_steel_appliances',
        'walk_in_closet',
        'washer_dryer_in_unit'
      ]
        .filter((_) => availUnitAmenities.has(_))
        .map((_) => ({ label: this.toTitle(_), value: _ }))
    },
    amenityColumns(key: string) {
      const cKey = key as 'building_amenities' | 'unit_amenities'
      const sortedAmenities = this.amenitySelections[cKey].map((_) => _.value).sort()
      return [
        {
          name: cKey,
          label: this.toTitle(cKey),
          field: 'label',
          align: 'left',
          classes: 'constrained-cell'
        },
        ...sortedAmenities.map((_) => ({
          name: _,
          label: this.toTitle(_),
          field: _,
          align: 'center'
        }))
      ] as QTableColumn[]
    },
    amenityRows(key: string) {
      const cKey = key as 'building_amenities' | 'unit_amenities'
      return this.filteredProperties.flatMap(({ prop }) => {
        const d = {
          id: prop.id,
          label: prop.building_name || prop.street_address
        } as { [k: string]: string }

        for (const a of prop[cKey] || []) {
          d[a] = '✓'
        }
        return [d]
      })
    },
    filterFn(val: string, update: (fn: () => void) => void) {
      update(() => {
        const needle = val.toLocaleLowerCase().trim()
        const normalizedNeedle = needle.replace(/\s+/g, '_')
        this.filteredAmenities = {
          building_amenities: this.availableAmenities.building_amenities
            .filter(
              (v: string) =>
                v.toLocaleLowerCase().includes(needle) ||
                v.toLocaleLowerCase().includes(normalizedNeedle) ||
                this.toTitle(v).toLocaleLowerCase().includes(needle)
            )
            .map((v: string) => ({ label: this.toTitle(v), value: v })),
          unit_amenities: this.availableAmenities.unit_amenities
            .filter(
              (v: string) =>
                v.toLocaleLowerCase().includes(needle) ||
                v.toLocaleLowerCase().includes(normalizedNeedle) ||
                this.toTitle(v).toLocaleLowerCase().includes(needle)
            )
            .map((v: string) => ({ label: this.toTitle(v), value: v }))
        }
      })
    },
    toTitle(label: string): string {
      return label
        .replace('avg_quality_score_', '')
        .replace('property_', '')
        .replace('quality', '')
        .split('_')
        .map((_) => _.slice(0, 1).toUpperCase() + _.slice(1))
        .join(' ')
    }
  }
}
</script>
<style scoped>
.constrained-cell {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
